import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Megaphone } from "lucide-react";
import api from "../../api/api";

const heroBg = "/assets/images/bg2.jpeg";

export default function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchAnnouncements = async () => {
    setLoading(true);

    try {
      const params = {
        status: "published",
        limit: 100,
      };

      if (activeCategory) {
        params.category_id = activeCategory;
      }

      const res = await api.get("/announcements", { params });
      setAnnouncements(res.data.items || []);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/announcement-categories");
      setCategories(res.data || []);
    } catch (error) {
      console.error("Failed to fetch announcement categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [activeCategory]);

  return (
    <main className="bg-white">
      <section
        className="relative min-h-[420px] flex items-center bg-cover bg-center"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="absolute inset-0 bg-[#061A40]/80" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 py-24 text-white">
          <p className="uppercase tracking-[0.35em] text-[#F4B000] font-black text-xs">
            Publications
          </p>

          <h1 className="text-4xl md:text-6xl font-black mt-4 max-w-4xl">
            Announcements
          </h1>

          <p className="mt-6 max-w-2xl text-white/80 leading-8">
            Official notices, public announcements and important updates from the Oil & Gas Free Zones Authority.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex flex-wrap gap-3 mb-10">
            <button
              onClick={() => setActiveCategory("")}
              className={`px-5 py-3 rounded-full font-bold text-sm ${
                activeCategory === ""
                  ? "bg-[#061A40] text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              All Announcements
            </button>

            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-5 py-3 rounded-full font-bold text-sm ${
                  activeCategory === category.id
                    ? "bg-[#061A40] text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-20 text-center text-slate-500 font-bold">
              Loading announcements...
            </div>
          ) : announcements.length === 0 ? (
            <div className="py-20 text-center bg-slate-50 rounded-3xl border border-slate-200">
              <Megaphone className="mx-auto text-slate-400" size={48} />
              <h2 className="mt-4 text-2xl font-black text-[#061A40]">
                No announcements found
              </h2>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {announcements.map((item) => (
                <article
                  key={item.id}
                  className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#F4B000]/15 text-[#061A40] flex items-center justify-center mb-5">
                    <Megaphone size={24} />
                  </div>

                  {item.category?.name && (
                    <p className="text-xs uppercase tracking-widest font-black text-[#F4B000] mb-3">
                      {item.category.name}
                    </p>
                  )}

                  <h2 className="text-xl font-black text-[#061A40] leading-snug">
                    {item.title}
                  </h2>

                  {item.summary && (
                    <p className="mt-4 text-slate-600 leading-7">
                      {item.summary}
                    </p>
                  )}

                  <Link
                    to={`/publications/announcements/${item.slug}`}
                    className="mt-6 inline-flex items-center gap-2 font-black text-[#061A40] hover:text-[#F4B000]"
                  >
                    {item.button_text || "Read Full Announcement"}
                    <ChevronRight size={18} />
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}